import React from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Alert, Button, Stack } from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import { toast } from "react-toastify";

export const ACCOUNT_ACCESS = gql`
  query AccountAccess($id: ID!) {
    user(id: $id) { id active }
  }
`;
export const SUSPEND_USER = gql`
  mutation SuspendUser($id: ID!) {
    suspendUser(id: $id) { id active }
  }
`;
export const RESTORE_USER_ACCESS = gql`
  mutation RestoreUserAccess($id: ID!) {
    restoreUserAccess(id: $id) { id active }
  }
`;

const AccountAccess: React.FC<{
  id: string;
  onComplete: (user: { id: string; active: boolean }) => void;
}> = ({ id, onComplete }) => {
  const confirm = useConfirm();
  const { data, loading, error } = useQuery(ACCOUNT_ACCESS, {
    variables: { id }, fetchPolicy: "network-only",
  });
  const [suspend, suspension] = useMutation(SUSPEND_USER);
  const [restore, restoration] = useMutation(RESTORE_USER_ACCESS);
  const busy = suspension.loading || restoration.loading;

  const changeAccess = async () => {
    const active = data?.user?.active;
    try {
      await confirm({
        title: active ? "Spärra kontot?" : "Återaktivera kontot?",
        description: active
          ? "Kontot spärras i alla VO-College-tjänster och tidigare inloggningar återkallas. Redan hämtade filer och utlämnade länkar påverkas inte. Osparade formulärändringar kastas."
          : "Återaktivera först när orsaken till spärren är åtgärdad. Användaren behöver logga in igen. Osparade formulärändringar kastas.",
        confirmationText: active ? "Spärra konto" : "Återaktivera konto",
        cancellationText: "Avbryt",
        confirmationButtonProps: { color: active ? "error" : "primary" },
      });
    } catch {
      return;
    }
    try {
      const result = await (active ? suspend : restore)({ variables: { id } });
      const user = active ? result.data?.suspendUser : result.data?.restoreUserAccess;
      if (!user) throw new Error("Åtgärden kunde inte bekräftas. Läs in sidan igen och kontrollera kontot.");
      toast.success(active ? "Kontot har spärrats." : "Kontot har återaktiverats. Ny inloggning krävs.");
      onComplete(user);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Åtgärden misslyckades.");
    }
  };

  if (loading) return <Alert severity="info">Kontrollerar kontots åtkomst…</Alert>;
  if (error || !data?.user) return <Alert severity="error">Kontots åtkomst kunde inte läsas. Ladda om sidan.</Alert>;

  return (
    <Alert severity={data.user.active ? "warning" : "info"} sx={{ mb: 2 }}>
      <Stack spacing={1} alignItems="flex-start">
        <span>{data.user.active
          ? "Vid misstänkt kontointrång kan du spärra kontot i samtliga VO-College-tjänster."
          : "Kontot är avaktiverat. Kontrollera incidenten innan du återaktiverar det."}</span>
        <Button variant="outlined" color={data.user.active ? "error" : "primary"}
          disabled={busy} onClick={changeAccess}>
          {busy ? "Arbetar…" : data.user.active ? "Spärra konto" : "Återaktivera konto"}
        </Button>
      </Stack>
    </Alert>
  );
};

export default AccountAccess;
